import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles, EVFormHelperText, EVBox, EVPhone,
} from '@eagleview/ev-comp-library';

const useStyles = makeStyles((theme) => ({
  container: {
    width: theme.spacing(38),
    marginTop: theme.spacing(3),
    border: theme.spacing(0),
    '& .vp-MuiBox-root': {
      '&:focus-within': {
        borderBottom: `${theme.spacing(0.3)}px solid ${theme.palette.primary.main}`,
      },
    },
  },
  text: {
    fontSize: theme.spacing(1.8),
    color: theme.evPalette.charcoal[700],
  },
  textRoot: {
    height: theme.spacing(7),
    width: theme.spacing(20),
    paddingTop: theme.spacing(1),
    border: theme.spacing(0),
    backgroundColor: theme.evPalette.nickel[100], // To be changed from 100 to 500 when color is added to theme
  },
  root: {
    backgroundColor: theme.evPalette.nickel[100], // To be changed from 100 to 500 when color is added to theme
    color: theme.evPalette.charcoal[900],
    border: theme.spacing(0),
    '& label': {
      marginTop: theme.spacing(1.5),
    },
  },
  autocomplete: {
    width: theme.spacing(18),
    height: theme.spacing(7),
    paddingTop: theme.spacing(1),
  },
}));

const PhoneNumberComponent = (props) => {
  const {
    countryList, userProfileDetails, handleBlur, userErrorMessages, handleChange,
  } = props;
  const [focus, setFocus] = useState(false);
  const [countryObj, setCountryObj] = useState(null);
  const [phoneText, setPhoneText] = useState(userProfileDetails.phoneNumber);
  const [formError, setFormError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [reset, setReset] = useState(0);
  const classes = useStyles();

  const autocompleteInputProps = {
    autoFocus: false,
    variant: 'outlined',
    label: 'Mobile number',
    classes: {
      root: classes.autocomplete,
    },
  };

  const textProps = {
    classes: {
      root: classes.textRoot,
    },
    InputProps: { ...{ inputProps: { maxLength: 15 }, autoFocus: focus } },
    onBlur: handleBlur,
  };

  useEffect(() => {
    const country = countryList.find((obj) => obj.id === userProfileDetails.countryId);
    setCountryObj(country);
  }, []);

  useEffect(() => {
    const helperText = userErrorMessages.countryId ? userErrorMessages.countryId : userErrorMessages.phoneNumber;
    if (helperText !== '') {
      setErrorMessage(helperText);
      setFormError(true);
    } else {
      setFormError(false);
    }
  }, [userErrorMessages]);

  const SearchMessage = () => <p className={classes.text}>Result not found</p>;

  const handleSelectChange = (value) => {
    if (value) {
      setCountryObj(value);
      const event = { target: { value: value.id } };
      handleChange('countryId', event);
      setFocus(true);
      setReset(reset ? 0 : 1);
    }
  };

  const handleTextChange = (event) => {
    if (Number(event.target.value) || event.target.value === '') {
      setPhoneText(event.target.value);
    }
    handleChange('phoneNumber', event);
  };

  const handelOptionLabel = (countryObject) => `+ ${countryObject.code} ${countryObject.name}`;

  const handleRenderOption = (countryObject) => `+ ${countryObject.code} ${countryObject.name}`;

  return (
    <>
      <EVBox key={reset} className={classes.container}>
        <EVPhone
          autocompleteId="countryId"
          autoCompleteValue={countryObj}
          restTextProps={textProps}
          autocompleteInputProps={autocompleteInputProps}
          data={countryList}
          handleSelectChange={handleSelectChange}
          textFieldId="phoneNumber"
          phoneVariant="filled"
          handleTextChange={(event) => handleTextChange(event)}
          textFieldValue={phoneText}
          noOptionsText={<SearchMessage />}
          title={countryObj ? `+ ${countryObj.code} ${countryObj.name}` : ''}
          phonePlaceholder=""
          classes={{ root: classes.root }}
          handleOptionLabel={handelOptionLabel}
          handleRenderOption={handleRenderOption}
        />
      </EVBox>
      {formError && (<EVFormHelperText id="phoneNumberHelperText" error>{errorMessage}</EVFormHelperText>)}
    </>
  );
};

PhoneNumberComponent.propTypes = {
  /**
   * @property {array} countryList - get country list.
   */
  countryList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  /**
   * @property {Object} userDetails - details of the logged in user.
   */
  userProfileDetails: PropTypes.shape().isRequired,
  /**
   * @property {Object} handleBlur - action to be performed when the text field on blur event is triggered
   */
  handleBlur: PropTypes.func.isRequired,
  /**
   * @property {Object} handleChange - action to be performed when the text field value is changed
   */
  handleChange: PropTypes.func.isRequired,
  /**
   * @property {Object} userErrorMessages - Object containing the list of error messages for the fields in the component
   */
  userErrorMessages: PropTypes.shape({
    phoneNumber: PropTypes.string,
    countryId: PropTypes.string,
  }).isRequired,
};

export default PhoneNumberComponent;
